<script>
  import { LayerCake, Svg, flatten } from "layercake";
  import { timeParse, timeFormat } from "d3-time-format";
  import { format } from "d3-format";
  import { scaleTime } from "d3-scale";
  import { csvParse } from "d3-dsv";

  const defNum = format(",.1f");

  import Flats from "./flat.svelte";
  import House from "./house.svelte";
  import Field from "./field.svelte";
  import Track from "./track.svelte";
  import Toggle from "./Toggle.svelte";
  import Bar from "./bar.svelte";
  import Bar2 from "./track.svelte";

  import msoaLookup from '../data/MSOA-Names.csv';

  import parksCountry from '../data/Country Parks only-Table 1.csv';
  import parksRegion from '../data/Region Parks only-Table 1.csv';
  import parksLad from '../data/LAD Parks only-Table 1.csv';
  
  import gardensCountry from '../data/Country gardens-Table 1.csv';
  import gardensRegion from '../data/Region gardens-Table 1.csv';
  import gardensLad from '../data/LAD gardens-Table 1.csv';
  import testLoc from '../data/testLoc.json';

  export let codes = {};
  export let data = {};
  // export let noOfFlats = 0;
  
  let gardenData = {};
  let parksData = {};

  let selectedDistrict = "";
  let districtName = "";
  let selectedLsoa = "";
  let selectedLA = "";
  let selectedRegion = "";
  let selectedCountry = "";

  $: parkRef = {
    country: [],
    region: [],
    la: []
  };

  $: gardenRef = {
    country: [],
    region: [],
    la: []
  };

  $: g = {};
  $: p = {};
  $: postcodeData = {};

  const imdDecile = [
    "(among the 10% most deprived areas in England)",
    "(among the 20% most deprived areas in England)",
    "(among the 30% most deprived areas in England)",
    "(among the 40% most deprived areas in England)",
    "(among the 50% most deprived areas in England)",
    "(among the 50% least deprived areas in England)",
    "(among the 40% least deprived areas in England)",
    "(among the 30% least deprived areas in England)",
    "(among the 20% least deprived areas in England)",
    "(among the 10% least deprived areas in England)"
  ]
  
  function processResponse(result, data) {
    selectedDistrict = result.lad;
    districtName = msoaLookup.filter(d => d["\ufeffmsoa11cd"] === result.msoa)[0].msoa11hclnm;
    postcodeData = result;
    selectedLsoa = result.lsoa;
    selectedLA = result.lad;
    selectedRegion = result.region;
    selectedCountry = result.country;

    parkRef.country = parksCountry.filter(d => d["Country code"] === selectedCountry).map(d => ({
      name: d["Country name"],
      ave: d["Average distance to nearest Park or Public Garden (m)"]
    }))

    parkRef.region = parksRegion.filter(d => d["Region code"] === selectedRegion).map(d => ({
      name: d["Region name"],
      ave: d["Average distance to nearest Park or Public Garden (m)"]
    }))
  
    parkRef.la = parksLad.filter(d => d["LAD code"] === selectedLA).map(d => ({
      name: d["LAD name"],
      ave: d["Average distance to nearest Park, Public Garden, or Playing Field (m)"]
    }))

    gardenRef.country = gardensCountry.filter(d => d["Country code"] === selectedCountry)[0];

    gardenRef.region = gardensRegion.filter(d => d["Region code"] === selectedRegion)[0];
  
    gardenRef.la = gardensLad.filter(d => d["LAD code"] === selectedLA)[0];

    g = csvParse(data.gardens).filter(d => d.field7 === result.msoa)[0];
    p = csvParse(data.parks).filter(d => d.field9 === selectedLsoa)[0];

    return true;
  }

  $: hasLoaded = processResponse(codes, data);

  $: lengths = [{
    l: defNum(+parkRef.la[0].ave),
    v: (+parkRef.la[0].ave),
    fill: "#E6B217",
    area: parkRef.la[0].name
  },{
    l: defNum(+parkRef.region[0].ave),
    v: (+parkRef.region[0].ave),
    fill: "#0063E6",
    area: parkRef.region[0].name
  },{
    l: defNum(+parkRef.country[0].ave),
    v: (+parkRef.country[0].ave),
    fill: "#c1002a",
    area: parkRef.country[0].name
  },{
    l: p.field13 ? defNum(+p.field13) : 0,
    v: p.field13 ? (+p.field13) : 0,
    fill: "#7ec850",
    area: districtName,
  }];

  $: maxParkLength = lengths.slice(0).map(d => d.v).sort((a, b) => b - a)[0];

  $: flatCount = g.field21;
  // $: flatCount = noOfFlats;

  let isIso = true;

	function toggleIso(newValue) {
		isIso = newValue.detail;
  }
</script>

<div class="container">
  <p><strong>{districtName}</strong> {imdDecile[+p.field12 - 1]}</p>
  <p><span class="area">{g.field25}</span> of all residences have access to a private garden, compared to <span class="country">{gardenRef.country.total_perc} in {gardenRef.country.Country},</span>
  <span class="region">{gardenRef.region.total_perc} in {gardenRef.region["Region name"]}, </span> and
  <span class="la">{gardenRef.la.total_perc} in {gardenRef.la["LAD name"]}</span></p>

  <div class:isIso class="graphic-container houses">
    <span class="area">{g.field12}</span> of houses in {districtName} have a private garden compared to
    <span class="country">{gardenRef.country.houses_perc},</span>
      <span class="region">{gardenRef.region.houses_perc},</span> and
      <span class="la">{gardenRef.la.houses_perc}</span>

    <p>Houses have a median average of <span class="area">{g.field14}m<sup>2</sup></span> private space.</p>
    
    <div aria-hidden="true" class="pic-container">
      <House {isIso} />
      <Field type="houses" data={gardenRef} {isIso} size={g.field14} {districtName} />
    </div>
    </div>
    <div class:isIso class="graphic-container">
    <span class="area">{g.field19}</span> of flats have access to private space, compared to 
    <span class="country">{gardenRef.country.flats_perc},</span>
      <span class="region">{gardenRef.region.flats_perc},</span> and
      <span class="la">{gardenRef.la.flats_perc}</span>
    <p>Flats have an average outdoor space of <span class="area">{g.field26}m<sup>2</sup></span>, shared by {defNum(+flatCount)} flats.</p>
    <div aria-hidden="true" class="pic-container" style="height:{isIso ? (flatCount > 20 ? 200 + Math.floor(flatCount / 20) * 30 : 200) : "200"}px">
      <Flats {isIso} count={flatCount} />
      <Field type="flats" data={gardenRef} {isIso} size={g.field26} {districtName} />
    </div>
  </div>

  <div class="isoCheck">
    <Toggle on:message={toggleIso} /> Toggle {toggleIso ? "flat" : "isometric"} view.
  </div>

  <div class="parks-summary">
    <p>Parks and Recreation: In <strong>{districtName}</strong>, the average distance to the nearest park is <span class="area">{(+p.field13).toFixed(0)}m</span>, and there {+p.field15 < 1.5 ? "is" : "are"} {(+p.field15).toFixed(0)} park{+p.field15 < 1.5 ? "" : "s"} within a 1km radius.</p>

    <Bar2 lines={lengths} formatTick={d => format(",.0f")(d) + "m"} />

    <ul class="legend">
      <li class="country">{lengths[2].area} - {lengths[2].l}m</li>
      <li class="region">{lengths[1].area} - {lengths[1].l}m</li>
      <li class="la">{lengths[0].area} - {lengths[0].l}m</li>
      <li class="area">{lengths[3].area} - {lengths[3].l}m</li>
    </ul>

  </div>
  <div class="source-text">Source: <a href="https://www.ons.gov.uk/economy/environmentalaccounts/datasets/accesstogardensandpublicgreenspaceingreatbritain">ONS</a></div>
</div>

<style>
  div.container {
    width: 100%;
    padding-left: 16px;
  }

  .graphic-container {
    width: 45%;
    min-width: 280px;
    float: right;
    padding: 8px 16px 8px 0;
    padding-top: 48px;
  }

  .graphic-container p {
    padding: 0;
    min-height: 140px;
  }

  .houses {
    float: left;
    padding-bottom: 30px;
  }

  @media (max-width: 660px) {
    .graphic-container {
      float: none;
      width: 75%;
      margin: 0 auto;
    }
  }

  .parks-summary {
    clear: both;
    padding-top: 48px;
  }

  .pic-container {
    position: relative;
    height: 200px;
    margin-bottom: 50px;
  }

  span {
    display: inline-block;
    padding: 4px;
  }

  span.country {
    background: #c1002a;
    color: #fff;
  }

  span.region {
    background: #0063E6;
    color: #fff;
  }

  span.la {
    background: #E6B217;
  }

  span.area {
    background: #7ec850;
  }

  li {
    padding-left: 8px;
  }

  li.country {
    border-left: 10px solid #c1002a;
  }

  li.region {
    border-left: 10px solid #0063E6;
  }

  li.la {
    border-left: 10px solid #E6B217;
  }

  li.area {
    border-left: 10px solid #7ec850;
  }

  ul {
    list-style: none;
    padding: 0;
  }

  ul.summary li {
    margin-bottom: 20px;
  }

  .legend {
    font-size: 0.75em;
  }

  .isoCheck {
    clear: both;
    padding-top: 20px;
  }
</style>